const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface ConstructionRegistration {
  id: string;
  apartmentId: string;
  buildingId: string;
  contractorName: string;
  contractorPhone: string;
  startDate: string;
  startTime?: string;
  endDate: string;
  endTime?: string;
  constructionContent: string;
  notes?: string;
  attachments: string[];
  agreeToRegulations: boolean;
  status: 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'IN_CONSTRUCTION' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
  depositAmount?: number;
  depositPaid: boolean;
  depositRefunded: boolean;
  depositCollectLedgerEntryId?: string | null;
  depositRefundLedgerEntryId?: string | null;
  rejectionReason?: string;
  createdById: string;
  confirmedById?: string;
  confirmedAt?: string;
  constructionStartedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  apartment: {
    code: string;
    ownerName: string;
    ownerPhone?: string;
  };
  building: {
    name: string;
  };
  createdBy: {
    name: string;
  };
  confirmedBy?: {
    name: string;
  };
}

export interface CreateConstructionRegistrationRequest {
  apartmentId: string;
  contractorName: string;
  contractorPhone: string;
  startDate: string;
  startTime?: string;
  endDate: string;
  endTime?: string;
  constructionContent: string;
  notes?: string;
  attachments?: string[];
  agreeToRegulations: boolean;
  depositAmount?: number;
}

export interface UpdateConstructionRegistrationRequest {
  apartmentId?: string;
  contractorName?: string;
  contractorPhone?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  constructionContent?: string;
  notes?: string;
  attachments?: string[];
  agreeToRegulations?: boolean;
  depositAmount?: number;
  status?: string;
  rejectionReason?: string;
  depositPaid?: boolean;
  depositRefunded?: boolean;
}

export interface ConstructionRegistrationQuery {
  buildingId?: string;
  apartmentId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedConstructionRegistrations {
  data: ConstructionRegistration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BuildingServiceSettings {
  id: string;
  serviceType: 'CONSTRUCTION' | 'ENTRY_EXIT' | 'MOVING';
  isActive: boolean;
  regulations?: string;
  depositAmount?: number;
  advanceBookingDays?: { min: number; max: number };
  staffAdvanceBookingDays?: { min: number; max: number };
  buildingId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServiceSettingsRequest {
  serviceType: 'CONSTRUCTION' | 'ENTRY_EXIT' | 'MOVING';
  isActive: boolean;
  regulations?: string;
  depositAmount?: number;
  advanceBookingDays?: { min: number; max: number };
  staffAdvanceBookingDays?: { min: number; max: number };
}

export interface UpdateServiceSettingsRequest {
  isActive?: boolean;
  regulations?: string;
  depositAmount?: number;
  advanceBookingDays?: { min: number; max: number };
  staffAdvanceBookingDays?: { min: number; max: number };
}

class BuildingServicesApi {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // Service Settings Methods
  async getServiceSettings(buildingId: string, serviceType: string): Promise<BuildingServiceSettings | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/settings/${buildingId}/${serviceType}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching service settings:', error);
      throw error;
    }
  }

  async createServiceSettings(buildingId: string, data: CreateServiceSettingsRequest): Promise<BuildingServiceSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/settings/${buildingId}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating service settings:', error);
      throw error;
    }
  }

  async updateServiceSettings(buildingId: string, serviceType: string, data: UpdateServiceSettingsRequest): Promise<BuildingServiceSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/settings/${buildingId}/${serviceType}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating service settings:', error);
      throw error;
    }
  }

  // Construction Registration Methods
  async createConstructionRegistration(data: CreateConstructionRegistrationRequest): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating construction registration:', error);
      throw error;
    }
  }

  async getConstructionRegistrations(query?: ConstructionRegistrationQuery): Promise<PaginatedConstructionRegistrations> {
    try {
      const params = new URLSearchParams();
      
      if (query) {
        Object.entries(query).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const url = `${API_BASE_URL}/building-services/construction${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching construction registrations:', error);
      throw error;
    }
  }

  async getConstructionRegistration(id: string): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching construction registration:', error);
      throw error;
    }
  }

  async updateConstructionRegistration(id: string, data: UpdateConstructionRegistrationRequest): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating construction registration:', error);
      throw error;
    }
  }

  async confirmConstruction(id: string, depositAmount?: number): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/confirm`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ depositAmount })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error confirming construction:', error);
      throw error;
    }
  }

  async rejectConstruction(id: string, rejectionReason: string): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/reject`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ rejectionReason })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error rejecting construction:', error);
      throw error;
    }
  }

  async previewCollectConstructionDeposit(
    id: string,
    data: {
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
      collectAmount?: number;
    } = {},
  ): Promise<{ html: string }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/building-services/construction/${id}/collect-deposit/preview`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error previewing construction deposit:', error);
      throw error;
    }
  }

  async collectDeposit(
    id: string,
    data: {
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
      collectAmount?: number;
    } = {},
  ): Promise<
    ConstructionRegistration & {
      apartmentLedgerEntryId?: string | null;
      journalNumber?: string | null;
    }
  > {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/collect-deposit`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error collecting deposit:', error);
      throw error;
    }
  }

  async startConstruction(id: string): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/start-construction`, {
        method: 'PATCH',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error starting construction:', error);
      throw error;
    }
  }

  async previewRefundConstructionDeposit(
    id: string,
    data: {
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
      refundAmount?: number;
    } = {},
  ): Promise<{ html: string }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/building-services/construction/${id}/refund-deposit/preview`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error previewing construction refund:', error);
      throw error;
    }
  }

  async refundDeposit(
    id: string,
    data: {
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
      refundAmount?: number;
    } = {},
  ): Promise<
    ConstructionRegistration & {
      apartmentLedgerEntryId?: string | null;
      journalNumber?: string | null;
    }
  > {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/refund-deposit`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error refunding deposit:', error);
      throw error;
    }
  }

  async completeConstruction(id: string): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/complete`, {
        method: 'PATCH',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing construction:', error);
      throw error;
    }
  }

  async getConstructionActivityLogs(id: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/activity-logs`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error loading construction activity logs:', error);
      throw error;
    }
  }

  async cancelConstruction(id: string): Promise<ConstructionRegistration> {
    try {
      const response = await fetch(`${API_BASE_URL}/building-services/construction/${id}/cancel`, {
        method: 'PATCH',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling construction:', error);
      throw error;
    }
  }

  private async patchAction<T>(url: string, body?: object): Promise<T> {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  private async fetchPaginated<T>(url: string, query?: Record<string, any>): Promise<{ data: T[]; total: number; page: number; limit: number; totalPages: number }> {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    const fullUrl = `${url}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  // Entry Exit Registration Methods
  async createEntryExitRegistration(data: CreateEntryExitRegistrationRequest): Promise<EntryExitRegistration> {
    const response = await fetch(`${API_BASE_URL}/building-services/entry-exit`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async getEntryExitRegistrations(query?: EntryExitRegistrationQuery): Promise<PaginatedEntryExitRegistrations> {
    return this.fetchPaginated<EntryExitRegistration>(`${API_BASE_URL}/building-services/entry-exit`, query);
  }

  async getEntryExitRegistration(id: string): Promise<EntryExitRegistration> {
    const response = await fetch(`${API_BASE_URL}/building-services/entry-exit/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async updateEntryExitRegistration(id: string, data: UpdateEntryExitRegistrationRequest): Promise<EntryExitRegistration> {
    const response = await fetch(`${API_BASE_URL}/building-services/entry-exit/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async confirmEntryExit(id: string): Promise<EntryExitRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/entry-exit/${id}/confirm`);
  }

  async rejectEntryExit(id: string, rejectionReason: string): Promise<EntryExitRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/entry-exit/${id}/reject`, { rejectionReason });
  }

  async startEntryExit(id: string): Promise<EntryExitRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/entry-exit/${id}/start`);
  }

  async completeEntryExit(id: string): Promise<EntryExitRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/entry-exit/${id}/complete`);
  }

  async cancelEntryExit(id: string): Promise<EntryExitRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/entry-exit/${id}/cancel`);
  }

  // Moving Registration Methods
  async createMovingRegistration(data: CreateMovingRegistrationRequest): Promise<MovingRegistration> {
    const response = await fetch(`${API_BASE_URL}/building-services/moving`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async getMovingRegistrations(query?: MovingRegistrationQuery): Promise<PaginatedMovingRegistrations> {
    return this.fetchPaginated<MovingRegistration>(`${API_BASE_URL}/building-services/moving`, query);
  }

  async getMovingRegistration(id: string): Promise<MovingRegistration> {
    const response = await fetch(`${API_BASE_URL}/building-services/moving/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async updateMovingRegistration(id: string, data: UpdateMovingRegistrationRequest): Promise<MovingRegistration> {
    const response = await fetch(`${API_BASE_URL}/building-services/moving/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  }

  async confirmMoving(id: string): Promise<MovingRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/moving/${id}/confirm`);
  }

  async rejectMoving(id: string, rejectionReason: string): Promise<MovingRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/moving/${id}/reject`, { rejectionReason });
  }

  async startMoving(id: string): Promise<MovingRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/moving/${id}/start`);
  }

  async completeMoving(id: string): Promise<MovingRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/moving/${id}/complete`);
  }

  async cancelMoving(id: string): Promise<MovingRegistration> {
    return this.patchAction(`${API_BASE_URL}/building-services/moving/${id}/cancel`);
  }
}

export interface EntryExitRegistration {
  id: string;
  apartmentId: string;
  buildingId: string;
  visitorName: string;
  visitorPhone: string;
  visitDate: string;
  startTime: string;
  endTime: string;
  purpose: string;
  notes?: string;
  attachments: string[];
  agreeToRegulations: boolean;
  status: 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
  rejectionReason?: string;
  createdById: string;
  confirmedById?: string;
  confirmedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  apartment: { code: string; ownerName: string; ownerPhone?: string };
  building: { name: string };
  createdBy: { name: string };
  confirmedBy?: { name: string };
}

export interface CreateEntryExitRegistrationRequest {
  apartmentId: string;
  visitorName: string;
  visitorPhone: string;
  visitDate: string;
  startTime: string;
  endTime: string;
  purpose: string;
  notes?: string;
  attachments?: string[];
  agreeToRegulations: boolean;
}

export interface UpdateEntryExitRegistrationRequest extends Partial<CreateEntryExitRegistrationRequest> {
  status?: string;
  rejectionReason?: string;
}

export interface EntryExitRegistrationQuery {
  buildingId?: string;
  apartmentId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedEntryExitRegistrations {
  data: EntryExitRegistration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface MovingRegistration {
  id: string;
  apartmentId: string;
  buildingId: string;
  movingCompanyName: string;
  movingCompanyPhone: string;
  movingDate: string;
  startTime: string;
  endTime: string;
  movingContent: string;
  notes?: string;
  attachments: string[];
  agreeToRegulations: boolean;
  status: 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
  rejectionReason?: string;
  createdById: string;
  confirmedById?: string;
  confirmedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  apartment: { code: string; ownerName: string; ownerPhone?: string };
  building: { name: string };
  createdBy: { name: string };
  confirmedBy?: { name: string };
}

export interface CreateMovingRegistrationRequest {
  apartmentId: string;
  movingCompanyName: string;
  movingCompanyPhone: string;
  movingDate: string;
  startTime: string;
  endTime: string;
  movingContent: string;
  notes?: string;
  attachments?: string[];
  agreeToRegulations: boolean;
}

export interface UpdateMovingRegistrationRequest extends Partial<CreateMovingRegistrationRequest> {
  status?: string;
  rejectionReason?: string;
}

export interface MovingRegistrationQuery {
  buildingId?: string;
  apartmentId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedMovingRegistrations {
  data: MovingRegistration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const buildingServicesApi = new BuildingServicesApi(); 